import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toApiDateTime } from '@/lib/api/date';
import { toLocalInput } from '@/lib/clock';
import { defaultStation, stationWithin } from '@/types/flightAccess';
import type { InternationalArrivalCriteria, InternationalArrivalSortField } from '@/types/internationalArrival';
import { isInternationalArrivalSortField } from '@/types/internationalArrival';

/**
 * The journal's state, held in the URL and nowhere else.
 *
 * Same rule as the flight board: THE URL IS THE STATE. It is read on render and
 * written on an action, never mirrored into React state — so a refresh keeps the
 * filters, a link carries them, and Back works without a line of code for it.
 */

const KEYS = {
  airport: 'airport',
  flt: 'flt',
  orgn: 'orgn',
  spot: 'spot',
  acType: 'acType',
  /** `active` | `withdrawn` | `all`. */
  state: 'state',
  /** The STA range, as local `datetime-local` values. Either end may stand alone. */
  from: 'from',
  to: 'to',
  page: 'page',
  rows: 'rows',
  sort: 'sort',
  dir: 'dir',
} as const;

export const ROWS_OPTIONS = [25, 50, 100, 200] as const;
const DEFAULT_ROWS = 50;

/**
 * Newest arrival first.
 *
 * Within any range, the end an operator wants is the recent one — and the default
 * range below is the last day, so page one opens on the latest arrivals.
 */
const DEFAULT_SORT: InternationalArrivalSortField = 'sta';

/** Which rows to show. `all` is the endpoint's own default, spelled out. */
export const INTERNATIONAL_ARRIVAL_STATES = ['active', 'withdrawn', 'all'] as const;
export type InternationalArrivalState = (typeof INTERNATIONAL_ARRIVAL_STATES)[number];

/**
 * Active by default, which the ENDPOINT does not do.
 *
 * `/international-arrival/filter` applies no `isActive` at all unless it is sent, so
 * withdrawn rows would otherwise sit in the journal unmarked. A board is about
 * flights that are happening.
 */
const DEFAULT_STATE: InternationalArrivalState = 'active';

/**
 * The range the journal opens on: the last day, ending now.
 *
 * The whole history is a heavy first query and almost never the question, so the
 * screen arrives already narrowed. It is a DEFAULT, not a floor — both boxes are
 * editable and clearing one widens the range in that direction.
 *
 * A MODULE-LEVEL FUNCTION, not an expression in the hook. Reading the clock during
 * render is impure and the project's React rules reject it by name; the same reason
 * `useScheduleParams` resolves its presets through `presetRange` rather than
 * inline. Called once per URL from inside the memo, so it is stable for as long as
 * the filters are.
 *
 * Note what this means for a tab left open: the window is fixed at the moment the
 * filters last changed, not rolling. For a journal being read that is the steadier
 * behaviour — a range that slid forward under the reader would renumber their pages
 * while they worked.
 */
const defaultRange = (): { from: string; to: string } => {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  return { from: toLocalInput(yesterday), to: toLocalInput(now) };
};

export interface InternationalArrivalParams {
  airport: string;
  flt: string;
  orgn: string;
  spot: string;
  acType: string;
  state: InternationalArrivalState;
  /**
   * The STA range, as local wall clock (`yyyy-MM-ddTHH:mm`).
   *
   * Neither is ever '' here: `defaultRange` fills both when the URL carries no
   * range. The ENDPOINT treats each end as optional, so clearing a box drops that
   * bound from the request and widens the range in that direction.
   */
  from: string;
  to: string;
  page: number;
  rows: number;
  sort: InternationalArrivalSortField;
  dir: 'ascending' | 'descending';
}

const isOneOf = <T extends string>(options: readonly T[], value: string | null): value is T =>
  value !== null && (options as readonly string[]).includes(value);

/**
 * @param stationsOnly The stations this reader may pick — their access's airports — or
 *   `null` for every station, exactly as on the board; see `stationWithin` in
 *   types/flightAccess.ts. Pass a stable array: it is a dependency of `params`.
 */
export const useInternationalArrivalParams = (stationsOnly: readonly string[] | null = null) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<InternationalArrivalParams>(() => {
    const text = (key: string) => searchParams.get(key)?.trim() ?? '';

    const rows = Number(searchParams.get(KEYS.rows));
    const page = Number(searchParams.get(KEYS.page));
    const sort = searchParams.get(KEYS.sort);
    const state = searchParams.get(KEYS.state);

    const fallback = defaultRange();

    return {
      airport: stationWithin(text(KEYS.airport), stationsOnly),
      flt: text(KEYS.flt),
      orgn: text(KEYS.orgn),
      spot: text(KEYS.spot),
      acType: text(KEYS.acType),
      state: isOneOf(INTERNATIONAL_ARRIVAL_STATES, state) ? state : DEFAULT_STATE,
      from: text(KEYS.from) || fallback.from,
      to: text(KEYS.to) || fallback.to,
      // A page or size that is not a positive number falls back rather than being
      // sent: `rows: 0` returns no rows beside a real total.
      page: Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1,
      rows: Number.isFinite(rows) && rows >= 1 ? Math.floor(rows) : DEFAULT_ROWS,
      // Validated here, because an unknown `sortBy` is a 500 from this endpoint.
      sort: isInternationalArrivalSortField(sort) ? sort : DEFAULT_SORT,
      dir: searchParams.get(KEYS.dir) === 'ascending' ? 'ascending' : 'descending',
    };
  }, [searchParams, stationsOnly]);

  /**
   * Applies a change.
   *
   * Any change but the page itself resets to page 1. A narrower filter otherwise
   * leaves the reader on a page that no longer exists, and this endpoint answers
   * with the LAST page rather than an empty one — which reads as the filter having
   * been ignored.
   */
  const update = useCallback(
    (changes: Partial<InternationalArrivalParams>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          for (const [key, value] of Object.entries(changes)) {
            if (value === '' || value === undefined || value === null) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }

          if (!('page' in changes)) next.delete(KEYS.page);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const reset = useCallback(
    () => setSearchParams(new URLSearchParams(), { replace: true }),
    [setSearchParams],
  );

  /**
   * Whether anything is narrowing the journal — what "Clear all" is for.
   *
   * The range is tested by whether the URL CARRIES it, not by whether `params` has
   * a value: since the default fills both ends, testing the values would leave the
   * button enabled forever and looking broken, because clearing the URL brings the
   * default straight back. The station is the same case for a reader with a single
   * airport: it is where the journal opens, not a filter.
   */
  const hasFilters = useMemo(
    () =>
      searchParams.has(KEYS.state) ||
      searchParams.has(KEYS.from) ||
      searchParams.has(KEYS.to) ||
      params.airport !== defaultStation(stationsOnly) ||
      [params.flt, params.orgn, params.spot, params.acType].some((value) => value !== ''),
    [params, searchParams, stationsOnly],
  );

  /** The request body for these params. */
  const criteria = useMemo<InternationalArrivalCriteria>(
    () => ({
      airportIataCode: params.airport,
      flt: params.flt,
      orgn: params.orgn,
      spot: params.spot,
      acType: params.acType,
      // `all` sends nothing, which is what the endpoint treats as no filter — the
      // one case where omitting a field is the intent rather than an oversight.
      ...(params.state === 'all' ? {} : { isActive: params.state === 'active' }),
      // A range on `sta`, which is the pair a day view wants: `ata` and `br` are
      // empty until a flight lands, and a range on an empty column silently drops
      // the row — so ranging on either would quietly mean "already happened".
      //
      // Each end goes independently, so one alone is a half-open range. The wall
      // clock the reader picked is sent unchanged: this backend parses a zoneless
      // `yyyy-MM-dd HH:mm:ss` in its OWN timezone, so converting to UTC first moves
      // the day. See lib/api/date.ts.
      ...(params.from ? { staStart: toApiDateTime(params.from) } : {}),
      ...(params.to ? { staEnd: toApiDateTime(params.to) } : {}),
      pageNum: params.page,
      rows: params.rows,
      sortBy: params.sort,
      sortDirection: params.dir,
    }),
    [params],
  );

  return { params, criteria, update, reset, hasFilters };
};
